
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../../components/auth/AuthModal';
import { useAuth } from '../../hooks/useAuth';
import SEOHead from '../../components/SEOHead';

export default function Home() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleDashboardClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowLoginModal(true);
    }
  };

  const handleProjectsClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowLoginModal(true);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Vibecode",
    "description": "Plataforma moderna para organização e gerenciamento de projetos. Crie, organize e acompanhe seus projetos de forma eficiente.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "BRL"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <SEOHead
        title="Vibecode - Organizador de Projetos"
        description="Plataforma moderna para organização e gerenciamento de projetos. Crie, organize e acompanhe seus projetos de forma eficiente."
        keywords="gerenciamento de projetos, organização, produtividade, colaboração, Vibecode"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`}
        ogTitle="Vibecode - Organizador de Projetos"
        ogDescription="Plataforma moderna para organização e gerenciamento de projetos. Crie, organize e acompanhe seus projetos de forma eficiente."
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`}
        ogImage={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg`}
        twitterTitle="Vibecode - Organizador de Projetos"
        twitterDescription="Plataforma moderna para organização e gerenciamento de projetos. Crie, organize e acompanhe seus projetos de forma eficiente."
        twitterImage={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg`}
        jsonLd={jsonLd}
      />

      {/* Header */}
      <header className="bg-white shadow-sm safe-area-inset-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/976342a1a7f845a6b252d7e9ff105311/16e7a8fdc06168ca25b5a1e1505b0ed1.png"
                alt="Vibecode Logo"
                className="w-8 h-8 mr-3 rounded object-cover"
              />
              <h1 className="text-xl sm:text-2xl font-bold text-indigo-600" style={{ fontFamily: "Pacifico, serif" }}>
                Vibecode
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <a href="#recursos" className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                Recursos
              </a>
              <a href="#sobre" className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                Sobre
              </a>
              <button 
                onClick={handleDashboardClick}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Meus Projetos
              </button>
            </nav>
            
            {/* Desktop Login Button */}
            <button
              onClick={() => setShowLoginModal(true)}
              className="hidden md:block bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Entrar
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <a href="#recursos" className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                  Recursos
                </a>
                <a href="#sobre" className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                  Sobre
                </a>
                <button 
                  onClick={handleDashboardClick}
                  className="text-left text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
                >
                  Meus Projetos
                </button>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Entrar
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20multiple%20computer%20screens%20showing%20project%20management%20dashboards%2C%20clean%20minimalist%20design%2C%20bright%20natural%20lighting%2C%20professional%20team%20collaboration%20environment%2C%20blue%20and%20white%20color%20scheme%2C%20high-tech%20atmosphere&width=1920&height=800&seq=hero-bg&orientation=landscape')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 to-white/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="w-full">
            <div className="max-w-2xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Organizadores de Projetos em 
                <span className="text-indigo-600"> Vibecode</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-8 leading-relaxed">
                Gerencie seus projetos de forma intuitiva e eficiente. Crie, organize e acompanhe o progresso de todos os seus projetos em uma plataforma moderna e fácil de usar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-indigo-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Iniciar Novo Projeto
                </button>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="border-2 border-indigo-600 text-indigo-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Acessar Projetos
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="recursos" className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Recursos Principais
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Tudo que você precisa para gerenciar seus projetos de forma eficiente
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center p-6 sm:p-8 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-folder-line text-2xl text-indigo-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Organização Intuitiva
              </h3>
              <p className="text-gray-600">
                Organize seus projetos com facilidade usando nossa interface intuitiva e moderna
              </p>
            </div>
            
            <div className="text-center p-6 sm:p-8 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-2xl text-indigo-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Colaboração em Equipe
              </h3>
              <p className="text-gray-600">
                Trabalhe em equipe com ferramentas de colaboração e comunicação integradas
              </p>
            </div>
            
            <div className="text-center p-6 sm:p-8 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors sm:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-bar-chart-line text-2xl text-indigo-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Acompanhamento de Progresso
              </h3>
              <p className="text-gray-600">
                Monitore o progresso dos seus projetos com relatórios detalhados e métricas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Por que escolher o Vibecode?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6">
                Nossa plataforma foi desenvolvida pensando na simplicidade e eficiência. 
                Com o Vibecode, você pode gerenciar todos os seus projetos em um só lugar, 
                desde a criação até a conclusão.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 text-xl mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">Interface moderna e intuitiva</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 text-xl mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">Colaboração em tempo real</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 text-xl mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">Relatórios detalhados</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 text-xl mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">Suporte técnico especializado</span>
                </li>
              </ul>
            </div>
            <div className="order-1 lg:order-2 lg:text-right">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20team%20working%20together%20on%20project%20management%20software%2C%20modern%20office%20environment%2C%20diverse%20group%20of%20people%20collaborating%20around%20computer%20screens%2C%20clean%20workspace%20with%20natural%20lighting%2C%20productivity%20and%20teamwork%20concept&width=600&height=400&seq=about-img&orientation=landscape"
                alt="Equipe trabalhando com Vibecode"
                className="rounded-lg shadow-lg object-cover w-full h-64 sm:h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Pronto para começar?
          </h2>
          <p className="text-lg sm:text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Junte-se a milhares de usuários que já estão organizando seus projetos com o Vibecode
          </p>
          <button
            onClick={() => setShowLoginModal(true)}
            className="bg-white text-indigo-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Começar Agora
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 safe-area-inset-bottom">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center mb-4">
                <img
                  src="https://static.readdy.ai/image/976342a1a7f845a6b252d7e9ff105311/16e7a8fdc06168ca25b5a1e1505b0ed1.png"
                  alt="Vibecode Logo"
                  className="w-6 h-6 mr-2 rounded object-cover"
                />
                <h3 className="text-lg sm:text-xl font-bold" style={{ fontFamily: "Pacifico, serif" }}>
                  Vibecode
                </h3>
              </div>
              <p className="text-gray-400 text-sm sm:text-base">
                A melhor plataforma para organizar e gerenciar seus projetos
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm sm:text-base">Recursos</h4>
              <ul className="space-y-2 text-gray-400 text-sm sm:text-base">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Dashboard</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Projetos</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Relatórios</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Integrações</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm sm:text-base">Suporte</h4>
              <ul className="space-y-2 text-gray-400 text-sm sm:text-base">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Central de Ajuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Tutoriais</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Contato</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm sm:text-base">Empresa</h4>
              <ul className="space-y-2 text-gray-400 text-sm sm:text-base">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Sobre Nós</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Política de Privacidade</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Termos de Uso</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-gray-400 text-sm sm:text-base">
            <p>&copy; 2024 Vibecode. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* AuthModal do Supabase */}
      <AuthModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        initialMode="login"
      />
    </div>
  );
}
